import { RefreshCw } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Button, cn } from "@nexus/ui";

interface AdminFilterBarProps {
  /**
   * The window every region below is measured over. First, because it scopes
   * the scope filters themselves — their option lists are drawn from it.
   */
  range?: ReactNode;
  /** An `AdminSearchInput`. Second, because it is the widest net. */
  search?: ReactNode;
  /** Selects and comboboxes, widest first. */
  scopes?: ReactNode;
  /** Toggles that add or drop a whole class of rows. */
  chips?: ReactNode;
  /**
   * Drops every filter in this bar at once. Rendered only when something is
   * set: a permanently visible Clear reads as a filter that is always on.
   */
  onClear?: () => void;
  /** True when at least one filter is set, which is what reveals Clear. */
  isFiltered?: boolean;
  /**
   * What the filters left, phrased in the page's own noun — "1,240 events".
   * The bar's only output, and the answer to "did that do anything".
   */
  resultCount?: ReactNode;
  /** Freshness or a failed read. Sits beside the control that fixes it. */
  status?: ReactNode;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  /** Anything a page must offer that is not a filter. Use sparingly. */
  actions?: ReactNode;
}

/**
 * One row, one order, on every tab: window, search, scopes, chips, clear, then
 * the result count — and, pushed to the right edge, status and refresh.
 *
 * The order is fixed by this file rather than by each page, which is the whole
 * point: an operator who learns where the window control is on one tab has
 * learned it on all four.
 */
export function AdminFilterBar({
  range,
  search,
  scopes,
  chips,
  onClear,
  isFiltered = false,
  resultCount,
  status,
  onRefresh,
  isRefreshing = false,
  actions,
}: AdminFilterBarProps) {
  const { t } = useTranslation();
  const hasTrailing = status != null || onRefresh != null || actions != null;

  return (
    <div className="flex shrink-0 flex-wrap items-center gap-2">
      {range}
      {search}
      {scopes}
      {chips}

      {onClear && isFiltered ? (
        <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={onClear}>
          {t("admin.layout.filters.clear", "Clear filters")}
        </Button>
      ) : null}

      {resultCount ? (
        // `aria-live` rather than a toast: the count is the feedback for a
        // filter change, and it is already on screen where the change was made.
        <span className="text-xs tabular-nums text-muted-foreground" aria-live="polite">
          {resultCount}
        </span>
      ) : null}

      {hasTrailing ? (
        <div className="ml-auto flex shrink-0 items-center gap-2">
          {status}
          {actions}
          {onRefresh ? (
            <Button
              variant="outline"
              size="sm"
              className="h-8 px-2 text-xs"
              onClick={onRefresh}
              disabled={isRefreshing}
              aria-label={t("admin.layout.filters.refresh", "Refresh")}
            >
              <RefreshCw
                className={cn(
                  "size-3.5",
                  isRefreshing && "animate-spin motion-reduce:animate-none",
                )}
                aria-hidden
              />
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
